import { forwardRef } from "react";
import {
  HelperText,
  TextField,
  TextFieldInput,
  TextFieldLabel,
} from "@hireart/component-library";
import { useField } from "formik";
import isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";

/**
 * FormTextField wires up TextField with Formik
 */
export const FormTextField = forwardRef(
  (
    {
      "aria-describedby": ariaDescribedBy = undefined,
      autoComplete = undefined,
      className = "",
      id = undefined,
      isDisabled = false,
      isLabelUnstyled = false,
      isOptional = false,
      label,
      name,
      onChange = () => {},
    },
    ref
  ) => {
    const [field, meta] = useField({ name });
    const inputId = id || name;
    const hasError = meta.touched && !isEmpty(meta.error);
    const helperTextIds = [
      hasError ? `${inputId}-error` : undefined,
      ariaDescribedBy,
    ]
      .filter(Boolean)
      .join(" ");

    const handleChange = (e) => {
      field.onChange(e);
      onChange(e, e.target.value);
    };

    return (
      <div className={className}>
        <TextField
          hasError={hasError}
          isDisabled={isDisabled}
          isOptional={isOptional}
        >
          <TextFieldLabel htmlFor={inputId} isUnstyled={isLabelUnstyled}>
            {label}
          </TextFieldLabel>
          <TextFieldInput
            {...field}
            ref={ref}
            aria-describedby={
              isEmpty(helperTextIds) ? undefined : helperTextIds
            }
            autoComplete={autoComplete}
            id={inputId}
            name={name}
            onChange={handleChange}
            size="md"
          />
        </TextField>
        {hasError && (
          <HelperText id={`${inputId}-error`} hasError>
            {meta.error}
          </HelperText>
        )}
      </div>
    );
  }
);

FormTextField.displayName = "FormTextField";

FormTextField.propTypes = {
  "aria-describedby": PropTypes.string,
  autoComplete: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  isDisabled: PropTypes.bool,
  isLabelUnstyled: PropTypes.bool,
  isOptional: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
};
